/**
 * Shared entrance-motion primitives, built on the app-wide `LazyMotion`
 * (`domAnimation`, strict) mounted in main.tsx — so only the lightweight `m`
 * component is used, never the full `motion` bundle.
 *
 *   • <Enter index>      — staggered fade + rise for items in a list/grid.
 *   • <Stagger>          — wraps a set of children, auto-assigning the stagger
 *                          index so callers don't thread it by hand.
 *   • <RouteTransition>  — keyed fade + rise replayed on every navigation.
 *
 * Everything collapses to an instant final state under prefers-reduced-motion
 * (the global guard in globals.css also neutralizes any residual transition).
 */
import { Children, isValidElement } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { m, useReducedMotion } from 'motion/react'

/** Max stagger steps — beyond this, items share the last delay (no long tails). */
const MAX_STAGGER = 8
/** Per-item stagger, in seconds. */
const STEP = 0.05

/** Fade + rise entrance; `index` drives the stagger. Instant under reduced motion. */
export function Enter({
  index = 0,
  children,
  style,
  className,
}: {
  index?: number
  children: ReactNode
  style?: CSSProperties
  className?: string
}) {
  const reduced = useReducedMotion() ?? false
  return (
    <m.div
      className={className}
      style={{ minWidth: 0, ...style }}
      initial={reduced ? false : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut', delay: Math.min(index, MAX_STAGGER) * STEP }}
    >
      {children}
    </m.div>
  )
}

/**
 * Wraps each direct child in an <Enter> with an auto-incrementing index, so a
 * vertical stack of sections cascades in. Each wrapper becomes the layout child
 * in its place — use only where children are independent blocks (not grid items
 * carrying their own column spans). For grids with explicit spans, wrap the
 * inner content with <Enter index={n}> directly instead.
 */
export function Stagger({
  children,
  className,
  style,
}: {
  children: ReactNode
  className?: string
  style?: CSSProperties
}) {
  let i = 0
  return (
    <div className={className} style={style}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child
        return <Enter index={i++}>{child}</Enter>
      })}
    </div>
  )
}

/**
 * Route-change transition. Key this on the active pathname so the element
 * remounts on navigation and replays the entrance — a calm cross-fade + rise
 * that makes page swaps feel intentional rather than instant. Enter-only (no
 * exit) keeps it robust against TanStack Router's synchronous outlet swap.
 */
export function RouteTransition({
  routeKey,
  children,
  style,
}: {
  routeKey: string
  children: ReactNode
  style?: CSSProperties
}) {
  const reduced = useReducedMotion() ?? false
  return (
    <m.div
      key={routeKey}
      style={{ minWidth: 0, ...style }}
      initial={reduced ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      {children}
    </m.div>
  )
}
